import React from "react";
import { Input, Button, Select } from "antd";
import {
  DeleteFilled,
  PlusCircleFilled,
  ArrowRightOutlined,
} from "@ant-design/icons";
import Typography from "app/shared/Typography";

interface ColumnMapping {
  id: number;
  sourceField: string;
  targetField: string;
  isPredefined?: boolean;
  isRequired?: boolean;
}

interface Props {
  mappings: ColumnMapping[];
  sourceFieldPlaceholders: Record<string, string>;
  addMappingPair: () => void;
  removeMapping: (id: number) => void;
  handleMappingChange: (
    id: number,
    field: keyof ColumnMapping,
    value: string,
  ) => void;
  fileColumns: string[];
  bookingIds: string[];
  onBookingIdsChange: (val: string[]) => void;
  disabled?: boolean;
}

const ColumnMappingTab: React.FC<Props> = ({
  mappings,
  sourceFieldPlaceholders,
  addMappingPair,
  removeMapping,
  handleMappingChange,
  fileColumns,
  bookingIds,
  onBookingIdsChange,
  disabled = false,
}) => {
  return (
    <div className="ColumnMapping">
      <Typography
        variant="h5"
        weight={500}
        style={{ marginBottom: 16, marginTop: 24, display: "flex " }}
      >
        Column Mapping
        <div className="actions-container" style={{ marginTop: 0 }}>
          <Button
            icon={<PlusCircleFilled />}
            onClick={addMappingPair}
            className="add-flow-button"
            disabled={disabled}
          >
            Add more
          </Button>
        </div>
      </Typography>

      {mappings.map((mapping) => (
        <div key={mapping.id} className="FormRow">
          <div className="FormGroup">
            <Typography variant="xs" style={{ marginBottom: 3 }}>
              Source Field{" "}
              {mapping.isPredefined && <span style={{ color: "red" }}>*</span>}
            </Typography>
            <Input
              value={sourceFieldPlaceholders[mapping.sourceField] || ""}
              onChange={(e) =>
                handleMappingChange(mapping.id, "sourceField", e.target.value)
              }
              disabled={disabled || mapping.isPredefined}
              placeholder={
                sourceFieldPlaceholders[mapping.sourceField] ||
                "Enter source field"
              }
              style={{ height: 38 }}
            />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "0 8px",
              marginTop: 24,
            }}
          >
            <ArrowRightOutlined style={{ fontSize: 18 }} />
          </div>

          <div className="FormGroup">
            <Typography variant="xs" style={{ marginBottom: 3 }}>
              Target Field
            </Typography>
            {fileColumns.length > 0 ? (
              <Select
                showSearch
                allowClear
                value={mapping.targetField || undefined}
                placeholder="Select column"
                style={{ width: "100%", height: 38 }}
                disabled={disabled}
                options={[
                  { label: "Null", value: "__null__" },
                  ...fileColumns.map((col) => ({ label: col, value: col })),
                ]}
                onChange={(val) =>
                  handleMappingChange(mapping.id, "targetField", val ?? "")
                }
              />
            ) : (
              <Input
                value={mapping.targetField}
                onChange={(e) =>
                  handleMappingChange(mapping.id, "targetField", e.target.value)
                }
                disabled={disabled}
                style={{ height: 38 }}
              />
            )}
          </div>

          {!mapping.isPredefined && (
            <Button
              danger
              type="text"
              icon={<DeleteFilled />}
              onClick={() => removeMapping(mapping.id)}
              disabled={disabled}
              style={{ marginTop: 25 }}
            />
          )}
        </div>
      ))}

      {/* Unique ID — mapping row layout, multi-select target */}
      <div className="FormRow" style={{ marginTop: 24 }}>
        <div className="FormGroup">
          <Typography variant="xs" style={{ marginBottom: 3 }}>
            Source Field <span style={{ color: "red" }}>*</span>
          </Typography>
          <Input value="Unique ID" disabled style={{ height: 38 }} />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0 8px",
            marginTop: 24,
          }}
        >
          <ArrowRightOutlined style={{ fontSize: 18 }} />
        </div>

        <div className="FormGroup">
          <Typography variant="xs" style={{ marginBottom: 3 }}>
            Target Field <span style={{ color: "red" }}>*</span>
          </Typography>
          <Select
            mode="multiple"
            showSearch
            allowClear
            value={bookingIds}
            placeholder={
              fileColumns.length > 0
                ? "Select one or more columns"
                : "ie: INV-001, booking_id"
            }
            style={{ width: "100%", minHeight: 38 }}
            options={fileColumns.map((col) => ({ label: col, value: col }))}
            onChange={onBookingIdsChange}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
};

export default ColumnMappingTab;
